// src/pages/ImportClients.jsx - Beautiful Step-by-Step Import Wizard
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { bulkImportClients, getImportHistory, reverseImport, checkDuplicates, updateClient } from "../api";
import DuplicateMergeModal from "../components/DuplicateMergeModal";

import { useNavigate } from "react-router-dom";
import {
  Upload, Download, AlertCircle, CheckCircle, Info, FileSpreadsheet,
  ArrowRight, ArrowLeft, Check, X, Sparkles, Users, Shield,
  FileCheck, AlertTriangle, Loader2, Eye, ChevronDown, ChevronUp,
  History, RotateCcw, Clock
} from "lucide-react";

// All mappable fields with descriptions
const FIELD_GROUPS = {
  required: [
    { key: "fullName", label: "Full Name", desc: "Client's full name (will auto-split into first/last)", required: true },
    { key: "firstName", label: "First Name", desc: "Client's first name", required: true },
    { key: "lastName", label: "Last Name", desc: "Client's last name", required: true },
  ],
  contact: [
    { key: "phone", label: "Phone", desc: "Primary phone number (used for duplicate matching)", critical: true },
    { key: "email", label: "Email", desc: "Email address (also used for duplicate matching if no phone)" },
  ],
  policy: [
    { key: "effectiveDate", label: "Effective Date", desc: "Policy start date (MM/DD/YYYY)", critical: true },
    { key: "terminationDate", label: "Termination Date", desc: "Policy end date - if set, marks client as future inactive" },
    { key: "termReason", label: "Term Reason", desc: "Reason for termination (e.g., Disenrollment, Plan Change)" },
    { key: "carrier", label: "Carrier", desc: "Insurance carrier name" },
    { key: "plan", label: "Plan Name", desc: "Specific plan name" },
    { key: "planType", label: "Plan Type", desc: "e.g., Medicare Advantage, Supplement" },
    { key: "status", label: "Status", desc: "active, inactive, or lost" },
  ],
  personal: [
    { key: "dob", label: "Date of Birth", desc: "MM/DD/YYYY format" },
    { key: "address", label: "Address", desc: "Street address" },
    { key: "city", label: "City", desc: "City name" },
    { key: "state", label: "State", desc: "State abbreviation (e.g., KY)" },
    { key: "zip", label: "ZIP Code", desc: "5-digit ZIP" },
  ],
  other: [
    { key: "notes", label: "Notes", desc: "Any additional notes about the client" },
  ],
};

const ALL_FIELDS = Object.values(FIELD_GROUPS).flat();
const MAP_KEY = "proirp_import_map_clients";

// Smart auto-mapping with fuzzy matching
// Supports: UHC (memberFirstName), Anthem (Client Name), Humana, Aetna, Cigna, etc.
function guessMap(headers) {
  const map = {};
  headers.forEach((h, i) => {
    const key = String(h || "").toLowerCase().trim().replace(/[^a-z0-9]/g, '');

    // Names - check for separate first/last first
    // UHC: memberFirstName, memberLastName
    // Generic: first_name, firstName, First Name
    if (/firstname|fname|first|memberfirst/.test(key) && !/last/.test(key)) map.firstName = i;
    if (/lastname|lname|last|memberlast/.test(key) && !/first/.test(key)) map.lastName = i;

    // Full name column (single "name", "client name", "member name", "insured name", etc.)
    // Anthem: Client Name, Humana: Member Name
    if (/^name$|^clientname$|^membername$|^insuredname$|^fullname$|^customername$|^subscribername$|^beneficiaryname$/.test(key)) {
      map.fullName = i;
    }

    // Contact
    if (/email|mail/.test(key) && !/mailing/.test(key)) map.email = i;
    if (/phone|mobile|cell|tel/.test(key)) map.phone = i;

    // Policy - Anthem: Effective Date, Plan Name; UHC: effectiveDate, planName
    if (/effective|effdate|startdate|policystart|coveragestart/.test(key)) map.effectiveDate = i;
    // Termination date: term date, end date, termination date, disenrollment date
    if (/termination|termdate|enddate|disenroll|canceldate|termdt/.test(key)) map.terminationDate = i;
    // Term reason: termination reason, disenrollment reason, cancel reason
    if (/termreason|disenrollreason|cancelreason|endreason|terminationreason/.test(key)) map.termReason = i;
    if (/carrier|insurance|insurer|company|payer/.test(key)) map.carrier = i;
    // planType: Product (Anthem), planType, type
    if (/plantype|^type$|^product$/.test(key)) map.planType = i;
    // plan: Plan Name (Anthem), planName, plan
    else if (/planname|^plan$/.test(key)) map.plan = i;
    // Status: catch many variations - Anthem uses "Status", UHC "Member Status", etc.
    if (/status|active|inactive|enrollment/.test(key) && !/address/.test(key)) map.status = i;

    // Personal
    if (/dob|birth|birthdate|dateofbirth/.test(key)) map.dob = i;
    if (/address|street/.test(key) && !/email|mailing/.test(key)) map.address = i;
    if (/^city$/.test(key)) map.city = i;
    if (/^state$/.test(key)) map.state = i;
    if (/zip|postal/.test(key)) map.zip = i;

    // Other
    if (/note|comment|memo/.test(key)) map.notes = i;
  });

  return map;
}

// Searchable carrier dropdown component
function CarrierSearchDropdown({ carriers, value, onChange, placeholder = "Search carriers..." }) {
  const [inputValue, setInputValue] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  useEffect(() => { setInputValue(value || ""); }, [value]);
  useEffect(() => {
    function handleClickOutside(e) {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target) && inputRef.current && !inputRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handleInputChange(e) {
    const val = e.target.value;
    setInputValue(val);
    if (val.length >= 1) {
      const filtered = carriers.filter(c => c.toLowerCase().includes(val.toLowerCase())).slice(0, 12);
      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
      onChange(""); // Clear selection when input is cleared
    }
  }

  function selectCarrier(c) {
    setInputValue(c);
    onChange(c);
    setSuggestions([]);
    setShowSuggestions(false);
  }

  function handleFocus() {
    if (inputValue.length >= 1) {
      const filtered = carriers.filter(c => c.toLowerCase().includes(inputValue.toLowerCase())).slice(0, 12);
      if (filtered.length > 0) setShowSuggestions(true);
    } else {
      // Show top carriers when focused with empty input
      setSuggestions(carriers.slice(0, 12));
      setShowSuggestions(true);
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && suggestions.length > 0) {
      e.preventDefault();
      selectCarrier(suggestions[0]);
    }
    if (e.key === "Escape") setShowSuggestions(false);
  }

  function clearSelection() {
    setInputValue("");
    onChange("");
    setSuggestions([]);
    setShowSuggestions(false);
    inputRef.current?.focus();
  }

  return (
    <div className="relative w-full md:w-64">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          className="w-full border border-indigo-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 pr-8"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
        />
        {inputValue && (
          <button
            type="button"
            onClick={clearSelection}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X size={16} />
          </button>
        )}
      </div>
      {showSuggestions && suggestions.length > 0 && (
        <div ref={suggestionsRef} className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-64 overflow-auto">
          {suggestions.map((c, i) => (
            <button
              key={i}
              type="button"
              onClick={() => selectCarrier(c)}
              className="w-full text-left px-3 py-2 hover:bg-indigo-50 text-sm border-b border-gray-100 last:border-0"
            >
              {c}
            </button>
          ))}
        </div>
      )}
      <p className="text-xs text-indigo-500 mt-1">Type to search {carriers.length} carriers</p>
    </div>
  );
}

// Validation helpers
function validateDate(val) {
  if (!val) return null;
  const str = String(val).trim();

  // Check if it's an Excel serial date (number between 1 and 100000)
  // Excel dates start from Jan 1, 1900 (serial 1)
  // 45658 = Jan 1, 2025, so we check for reasonable range
  const num = parseFloat(str);
  if (!isNaN(num) && num > 1 && num < 100000 && str === String(num)) {
    // Convert Excel serial date to JavaScript date
    // Excel's epoch is Dec 30, 1899 (accounting for Excel's leap year bug)
    const excelEpoch = new Date(1899, 11, 30);
    const jsDate = new Date(excelEpoch.getTime() + num * 24 * 60 * 60 * 1000);
    return jsDate.toISOString().split('T')[0];
  }

  // Try standard date parsing
  const parsed = new Date(str);
  return !isNaN(parsed.getTime()) ? parsed.toISOString().split('T')[0] : null;
}

// Smart-guess if a status value likely means "inactive"
// Used for initial suggestion, but user can override
function guessIfInactive(val) {
  if (!val) return false;
  const str = String(val).toLowerCase().trim();

  // Likely ACTIVE patterns
  const activePatterns = ['active', 'a', 'yes', 'y', '1', 'true', 'enrolled', 'current', 'eff', 'effective'];
  if (activePatterns.includes(str)) return false;
  if (str.startsWith('act') || str.startsWith('enr') || str.startsWith('eff')) return false;

  // Likely INACTIVE patterns
  const inactivePatterns = ['inactive', 'i', 'no', 'n', '0', 'false', 'termed', 'terminated',
    'cancelled', 'canceled', 'lost', 'churned', 'lapsed', 'dropped', 'disenrolled',
    'expired', 'ended', 'closed', 'void', 'deleted', 'removed', 'pending', 'suspended'];
  if (inactivePatterns.includes(str)) return true;
  if (str.startsWith('inact') || str.startsWith('term') || str.startsWith('canc') ||
      str.startsWith('dis') || str.startsWith('exp') || str.startsWith('end')) return true;

  // Unknown - default to NOT inactive (safer to import than to skip)
  return false;
}

// Normalize status to active/inactive based on user classification or smart guess
function validateStatus(val, inactiveSet) {
  if (!val) return "active";
  const str = String(val).trim();

  // If user has classified this value, use their classification
  if (inactiveSet && inactiveSet.has(str.toLowerCase())) {
    return "inactive";
  }

  // Otherwise use smart guess
  return guessIfInactive(str) ? "inactive" : "active";
}

// Auto-detect carrier from plan name (common carrier prefixes)
const CARRIER_PATTERNS = [
  { pattern: /^anthem/i, carrier: 'Anthem' },
  { pattern: /^united|^uhc/i, carrier: 'UnitedHealthcare' },
  { pattern: /^humana/i, carrier: 'Humana' },
  { pattern: /^aetna/i, carrier: 'Aetna' },
  { pattern: /^cigna/i, carrier: 'Cigna' },
  { pattern: /^blue\s*cross|^bcbs|^blue\s*shield/i, carrier: 'Blue Cross Blue Shield' },
  { pattern: /^kaiser/i, carrier: 'Kaiser Permanente' },
  { pattern: /^centene|^wellcare/i, carrier: 'Centene/WellCare' },
  { pattern: /^molina/i, carrier: 'Molina Healthcare' },
  { pattern: /^cvs|^silverscript/i, carrier: 'CVS Health' },
  { pattern: /^devoted/i, carrier: 'Devoted Health' },
  { pattern: /^clover/i, carrier: 'Clover Health' },
  { pattern: /^oscar/i, carrier: 'Oscar Health' },
  { pattern: /^mutual\s*of\s*omaha/i, carrier: 'Mutual of Omaha' },
  { pattern: /^careplus/i, carrier: 'CarePlus Health' },
];

function detectCarrierFromPlan(planName) {
  if (!planName) return null;
  const plan = String(planName).trim();
  for (const { pattern, carrier } of CARRIER_PATTERNS) {
    if (pattern.test(plan)) {
      return carrier;
    }
  }
  return null;
}

// Step indicator component
function StepIndicator({ currentStep, steps }) {
  return (
    <div className="flex items-center justify-center mb-8">
      {steps.map((step, idx) => (
        <React.Fragment key={idx}>
          <div className="flex flex-col items-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
              idx < currentStep
                ? 'bg-green-500 text-white'
                : idx === currentStep
                  ? 'bg-indigo-600 text-white ring-4 ring-indigo-200'
                  : 'bg-gray-200 text-gray-500'
            }`}>
              {idx < currentStep ? <Check size={20} /> : idx + 1}
            </div>
            <span className={`text-xs mt-2 font-medium ${
              idx <= currentStep ? 'text-indigo-600' : 'text-gray-400'
            }`}>
              {step}
            </span>
          </div>
          {idx < steps.length - 1 && (
            <div className={`w-16 h-1 mx-2 rounded ${
              idx < currentStep ? 'bg-green-500' : 'bg-gray-200'
            }`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

export default function ImportClients() {
  const nav = useNavigate();
  const fileInputRef = useRef(null);

  // Wizard state
  const [step, setStep] = useState(0);
  const steps = ["Upload File", "Map Columns", "Review Data", "Import"];

  // File state
  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [map, setMap] = useState({});
  const [isDragging, setIsDragging] = useState(false);

  // UI state
  const [remember, setRemember] = useState(true);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [warnings, setWarnings] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [validationIssues, setValidationIssues] = useState([]);
  const [defaultCarrier, setDefaultCarrier] = useState("");

  // Recent imports state
  const [importHistory, setImportHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [reversingId, setReversingId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  // Skipped clients breakdown
  const [skippedClients, setSkippedClients] = useState([]);
  const [showSkippedModal, setShowSkippedModal] = useState(false);

  // Status value classification - let user define which values mean inactive
  const [statusValues, setStatusValues] = useState([]); // {value, count, isInactive}
  const [showStatusModal, setShowStatusModal] = useState(false);

  // Duplicate detection state
  const [potentialDuplicates, setPotentialDuplicates] = useState([]);  // Array of duplicate matches
  const [resolvedDuplicates, setResolvedDuplicates] = useState({});    // { importIndex: mergedRecord }
  const [skippedDuplicates, setSkippedDuplicates] = useState(new Set()); // Set of importIndex to skip
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [currentDuplicateIndex, setCurrentDuplicateIndex] = useState(0);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);

  // Potential inactives state (clients missing from re-imported book)
  const [potentialInactives, setPotentialInactives] = useState([]);  // Clients not in new import
  const [importCarrier, setImportCarrier] = useState(null);          // Detected carrier being imported
  const [clientsToMarkInactive, setClientsToMarkInactive] = useState(new Set()); // Client IDs to mark inactive
  const [dismissedInactives, setDismissedInactives] = useState(new Set());       // Client IDs user dismissed

  // Full carriers list (same as ClientFormFull.jsx)
  const CARRIERS = ["Aetna","Alignment Health Plan","Allwell","Amerigroup","Anthem Blue Cross Blue Shield","Asuris Northwest Health","Aultcare","Avera Health Plans","AvMed","Banner Health","BCBS of Alabama","BCBS of Arizona","BCBS of Arkansas","BCBS of Delaware","BCBS of Florida (Florida Blue)","BCBS of Georgia","BCBS of Hawaii","BCBS of Idaho","BCBS of Illinois","BCBS of Indiana","BCBS of Iowa","BCBS of Kansas","BCBS of Kansas City","BCBS of Louisiana","BCBS of Maine","BCBS of Massachusetts","BCBS of Michigan","BCBS of Minnesota","BCBS of Mississippi","BCBS of Montana","BCBS of Nebraska","BCBS of New Mexico","BCBS of North Carolina","BCBS of North Dakota","BCBS of Oklahoma","BCBS of Rhode Island","BCBS of South Carolina","BCBS of Tennessee","BCBS of Texas","BCBS of Vermont","BCBS of Wyoming","Blue Cross Blue Shield","Blue Shield of California","Bright Health","Capital Health Plan","Care N Care","Care Plus Health Plans","CareFirst BCBS","CareMore Health","CareOregon","CareSource","Cariten Health Plan","Centene","Centennial Care","Central Health Plan","Chinese Community Health Plan","CHRISTUS Health Plan","Cigna","Clover Health","Community Health Choice","Community Health Plan of Washington","Coventry Health Care","Dean Health Plan","Denver Health Medical Plan","Devoted Health","Elderplan","Emblem Health","EmblemHealth","Empire BCBS","Envision Healthcare","Essence Healthcare","Excellus BCBS","Fallon Health","Fidelis Care","First Health Part D","Florida Blue","Geisinger Health Plan","Global Health","Golden Rule Insurance","Group Health Cooperative","HAP (Health Alliance Plan)","Harvard Pilgrim Health Care","Health Alliance","Health First Health Plans","Health Net","Health New England","Health Partners","Health Plan of Nevada","Health Plan of San Joaquin","Health Plan of San Mateo","HealthMarkets","HealthPartners","HealthPlus of Michigan","HealthSpring","HealthSun Health Plans","Highmark BCBS","Highmark Delaware","Highmark West Virginia","Horizon BCBS of New Jersey","Humana","IEHP (Inland Empire Health Plan)","Independence Blue Cross","Independent Health","InnovAge","Integra Health","Kaiser Permanente","Kaiser Permanente Colorado","Kaiser Permanente Georgia","Kaiser Permanente Hawaii","Kaiser Permanente Mid-Atlantic","Kaiser Permanente Northern California","Kaiser Permanente Northwest","Kaiser Permanente Southern California","Kaiser Permanente Washington","L.A. Care Health Plan","Lasso Healthcare","Leon Health","Longevity Health Plan","Louisiana Healthcare Connections","Martin's Point Health Care","Medica","Medical Associates Health Plan","Medical Mutual of Ohio","MedicareBlue Rx","Medigold","Mercy Care","Mercy Health Plans","Meridian Health Plan","Molina Healthcare","MolinaHealthcare","Mount Carmel Health Plan","MVP Health Care","Neighborhood Health Plan","Network Health","Nova Healthcare","Oscar Health","Oxford Health Plans","PACE","PacificSource Health Plans","Paramount Insurance","Passport Health Plan","Peoples Health","Physicians Health Plan","Physicians Mutual","Piedmont Community Health Plan","Premera Blue Cross","Presbyterian Health Plan","Priority Health","Providence Health Plan","Quartz Health Solutions","Regence BlueCross BlueShield","Regence BlueShield of Idaho","Rocky Mountain Health Plans","SCAN Health Plan","Scott and White Health Plan","Secure Horizons","Security Health Plan","SelectCare","SelectHealth","SelectQuote","Sendero Health Plans","SilverScript","SilverSummit Health Plan","Simply Healthcare","Solis Health Plans","Soundpath Health","Sparrow PHP","Summa Care","SummaCare","Sunshine Health","Superior HealthPlan","TEFRA","Total Health Care","Touchstone Health","Tufts Health Plan","UCare","UHCP (University Health Care Plans)","Umpqua Health","UniCare","United American Insurance","UnitedHealthcare","UPMC Health Plan","US Health and Life","Vantage Health Plan","Verda Healthcare","VillageMD","Virginia Premier Health Plan","Vista Health Plan","Vivida Health","WellCare","WellMed","Western Health Advantage","Windsor Health Plan","Other"];

  // Load import history
  const loadImportHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const data = await getImportHistory();
      setImportHistory(data);
    } catch (e) {
      console.error('[IMPORT] Failed to load import history:', e);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  // Load history when showHistory is toggled on
  useEffect(() => {
    if (showHistory && importHistory.length === 0) {
      loadImportHistory();
    }
  }, [showHistory, loadImportHistory, importHistory.length]);

  // Handle reversing an import
  const handleReverseImport = async (importId) => {
    if (!window.confirm('Are you sure you want to reverse this import? This will permanently delete all clients that were added in this batch.')) {
      return;
    }
    setReversingId(importId);
    try {
      const result = await reverseImport(importId);
      setMsg(`Successfully reversed import: ${result.deletedCount} clients removed`);
      // Refresh history
      await loadImportHistory();
    } catch (e) {
      setErr('Failed to reverse import: ' + (e?.message || 'Unknown error'));
    } finally {
      setReversingId(null);
    }
  };

  // Drag and drop handlers
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  }, []);

  function processFile(f) {
    if (!f) return;

    // Check file type - support CSV and Excel files
    const fileName = f.name.toLowerCase();
    const fileType = f.type?.toLowerCase() || '';

    const isCSV = fileName.endsWith('.csv') ||
                  fileType === 'text/csv' ||
                  fileType === 'application/csv' ||
                  fileType === 'text/plain' ||
                  fileType === '';

    const isExcel = fileName.endsWith('.xlsx') ||
                    fileName.endsWith('.xls') ||
                    fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                    fileType === 'application/vnd.ms-excel';

    if (!isCSV && !isExcel) {
      setErr(`Please upload a CSV or Excel file. You uploaded: ${f.name} (type: ${f.type || 'unknown'})`);
      return;
    }

    setFile(f);
    setErr("");
    setMsg("");
    setWarnings([]);
    setValidationIssues([]);

    if (isExcel) {
      // Parse Excel file
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });

          // Get the first sheet
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];

          // Convert to array of arrays - keep ALL rows for header detection
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });

          console.log('[Excel] Raw data rows:', jsonData?.length);
          console.log('[Excel] First 3 rows:', jsonData?.slice(0, 3));

          if (!jsonData || jsonData.length < 2) {
            setErr("No data found. Make sure your file has a header row and at least one data row.");
            return;
          }

          // Pass all rows to processImportData - it will find the header row
          processImportData(jsonData);
        } catch (error) {
          console.error('[Excel] Parse error:', error);
          setErr(`Failed to parse Excel file: ${error.message}`);
        }
      };
      reader.onerror = () => {
        setErr("Failed to read file");
      };
      reader.readAsArrayBuffer(f);
    } else {
      // Parse CSV file
      Papa.parse(f, {
        skipEmptyLines: false, // Keep empty lines so row indices match
        complete: (res) => {
          if (!res.data || res.data.length < 2) {
            setErr("No data found. Make sure your file has a header row and at least one data row.");
            return;
          }
          // Pass all rows to processImportData - it will find the header row
          processImportData(res.data);
        },
        error: (error) => {
          setErr(`Failed to parse file: ${error.message}`);
        }
      });
    }
  }

  // Detect if a row looks like a header row (has multiple non-empty, text-like columns)
  function looksLikeHeaderRow(row) {
    if (!row || row.length === 0) return false;

    // Count non-empty cells
    const nonEmptyCells = row.filter(cell => {
      const str = String(cell ?? '').trim();
      return str.length > 0;
    });

    // A header row should have at least 3 non-empty columns
    if (nonEmptyCells.length < 3) return false;

    // Check if cells look like headers (text, not dates or long text)
    const headerLikeCells = nonEmptyCells.filter(cell => {
      const str = String(cell).trim();
      // Headers are usually short (< 50 chars), not numbers, not dates
      if (str.length > 50) return false;
      // Check if it's a pure number
      if (/^\d+$/.test(str)) return false;
      // Check if it's a date-like value
      if (/^\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}$/.test(str)) return false;
      return true;
    });

    // At least 60% of non-empty cells should look like headers
    return headerLikeCells.length >= nonEmptyCells.length * 0.6;
  }

  // Common function to process import data from CSV or Excel
  function processImportData(allRows) {
    console.log('[Import] Total rows received:', allRows?.length);
    console.log('[Import] First 3 rows:', allRows?.slice(0, 3));

    if (!allRows || allRows.length < 2) {
      setErr("No data found. Make sure your file has a header row and at least one data row.");
      return;
    }

    // Find the actual header row (might not be the first row in Excel files)
    let headerRowIndex = 0;
    for (let i = 0; i < Math.min(5, allRows.length); i++) {
      if (looksLikeHeaderRow(allRows[i])) {
        headerRowIndex = i;
        console.log('[Import] Found header row at index:', i);
        break;
      }
    }

    // If first row doesn't look like headers, check if row 1+ has more columns
    if (headerRowIndex === 0 && allRows.length > 1) {
      const firstRowCols = (allRows[0] || []).filter(c => String(c ?? '').trim()).length;
      const secondRowCols = (allRows[1] || []).filter(c => String(c ?? '').trim()).length;
      if (secondRowCols > firstRowCols * 1.5 && secondRowCols >= 5) {
        headerRowIndex = 1;
        console.log('[Import] First row looks like title, using row 1 as header');
      }
    }

    const head = allRows[headerRowIndex];
    const data = allRows.slice(headerRowIndex + 1);

    console.log('[Import] Raw headers:', head);
    console.log('[Import] Raw headers length:', head?.length);
    console.log('[Import] First few headers:', head?.slice(0, 5));

    // Ensure headers are strings (Excel might return numbers, dates, etc.)
    const cleanHeaders = (head || []).map((h, idx) => {
      const str = String(h ?? '').trim();
      return str || `Column ${idx + 1}`; // Fallback for empty headers
    });

    console.log('[Import] Clean headers:', cleanHeaders);
    console.log('[Import] Clean headers length:', cleanHeaders.length);

    // Ensure all row cells are strings too
    const cleanRows = (data || []).map(row =>
      (row || []).map(cell => cell == null ? '' : String(cell).trim())
    );

    // Filter out empty rows
    const filteredRows = cleanRows.filter(row => row.some(cell => cell !== ''));

    console.log('[Import] Rows count:', filteredRows.length);
    console.log('[Import] First row:', filteredRows[0]);

    if (filteredRows.length === 0) {
      setErr("No data rows found after header. Make sure your file has client data.");
      return;
    }

    setHeaders(cleanHeaders);
    setRows(filteredRows);

    // Try saved mapping first, then auto-guess
    let initial = {};
    try {
      const saved = JSON.parse(localStorage.getItem(MAP_KEY) || "null");
      if (saved && Array.isArray(saved.__headers) && JSON.stringify(saved.__headers) === JSON.stringify(cleanHeaders)) {
        initial = saved;
      } else {
        initial = guessMap(cleanHeaders);
      }
    } catch {
      initial = guessMap(cleanHeaders);
    }
    setMap(initial);

    // Auto-advance to mapping step
    setStep(1);
  }

  function onFile(e) {
    const f = e.target.files?.[0];
    processFile(f);
  }

  // Validation on step 2 -> 3
  function validateMapping() {
    const issues = [];

    // Check required fields - need at least a name (full name OR first/last)
    const hasFullName = map.fullName != null;
    const hasFirstOrLast = map.firstName != null || map.lastName != null;

    if (!hasFullName && !hasFirstOrLast) {
      issues.push({ type: 'error', message: 'At least Full Name or First/Last Name must be mapped' });
    }

    // Check critical fields
    if (map.effectiveDate == null) {
      issues.push({
        type: 'warning',
        message: 'Effective Date is not mapped. Retention tracking and OEP cohort features will be limited.'
      });
    }

    // Phone/email is now optional (warning only) - we use name+address for duplicate detection
    if (map.email == null && map.phone == null) {
      issues.push({
        type: 'warning',
        message: 'No Phone or Email mapped. Duplicate detection will use Name + Address only. Communication features will be limited.'
      });
    }

    // Check for potential data issues
    let dateIssues = 0;
    let emptyNames = 0;

    rows.forEach((row, idx) => {
      const fullName = map.fullName != null ? row[map.fullName] : '';
      const firstName = map.firstName != null ? row[map.firstName] : '';
      const lastName = map.lastName != null ? row[map.lastName] : '';
      const effDate = map.effectiveDate != null ? row[map.effectiveDate] : '';

      // Check if we have any name at all
      const hasName = fullName?.trim() || firstName?.trim() || lastName?.trim();
      if (!hasName) {
        emptyNames++;
      }

      if (effDate && !validateDate(effDate)) {
        dateIssues++;
      }
    });

    if (emptyNames > 0) {
      issues.push({
        type: 'warning',
        message: `${emptyNames} row(s) have empty names and will be skipped`
      });
    }

    if (dateIssues > 0) {
      issues.push({
        type: 'warning',
        message: `${dateIssues} row(s) have invalid date formats that couldn't be parsed`
      });
    }

    setValidationIssues(issues);
    return !issues.some(i => i.type === 'error');
  }

  function onChangeMap(field, idx) {
    setMap(m => ({ ...m, [field]: idx === "" ? undefined : Number(idx) }));
  }

  // Detect unique status values when status column is mapped
  useEffect(() => {
    if (map.status == null || rows.length === 0) {
      setStatusValues([]);
      return;
    }

    const statusIdx = map.status;
    const counts = {};

    // Count occurrences of each unique status value
    rows.forEach(row => {
      const val = String(row[statusIdx] || '').trim();
      if (val) {
        counts[val] = (counts[val] || 0) + 1;
      }
    });

    // Convert to array with smart-guess for inactive
    const values = Object.entries(counts)
      .map(([value, count]) => ({
        value,
        count,
        isInactive: guessIfInactive(value) // Smart guess as default
      }))
      .sort((a, b) => b.count - a.count); // Sort by count descending

    setStatusValues(values);

    // If we detected any values that we guess are inactive, show the modal for confirmation
    const hasInactiveGuess = values.some(v => v.isInactive);
    const hasUnknownValues = values.some(v => {
      const str = v.value.toLowerCase();
      // Check if it's a known pattern
      const knownActive = ['active', 'a', 'yes', 'y', '1', 'true', 'enrolled', 'current'];
      const knownInactive = ['inactive', 'i', 'no', 'n', '0', 'false', 'termed', 'terminated', 'cancelled', 'canceled', 'lost', 'churned'];
      return !knownActive.includes(str) && !knownInactive.includes(str);
    });

    // Auto-show modal if there are inactive values or unknown values to classify
    if (hasInactiveGuess || hasUnknownValues) {
      setShowStatusModal(true);
    }
  }, [map.status, rows]);

  function downloadTemplate() {
    const header = [
      "First Name", "Last Name", "Email", "Phone",
      "Effective Date", "Carrier", "Plan", "Plan Type", "Status",
      "DOB", "Address", "City", "State", "ZIP",
      "Notes"
    ];
    const sample = [
      ["Jane", "Doe", "jane@example.com", "555-111-2222", "01/15/2024", "Humana", "Medicare Advantage Plus", "Medicare Advantage", "active", "03/12/1950", "123 Main St", "Louisville", "KY", "40202", "Prefers email contact"],
      ["John", "Smith", "john@example.com", "555-222-3333", "10/01/2023", "UnitedHealthcare", "AARP Medicare Supplement", "Supplement", "active", "07/22/1948", "456 Oak Ave", "Lexington", "KY", "40507", "Birthday in July"],
      ["Mary", "Johnson", "mary@example.com", "555-333-4444", "01/01/2024", "Aetna", "Silver Plan", "Medicare Advantage", "active", "11/05/1952", "789 Pine Rd", "Bowling Green", "KY", "42101", "Spanish speaker"],
    ];
    const csv = [header, ...sample].map(r => r.map(cell => {
      const v = String(cell ?? "").replace(/"/g, '""');
      return /[",\n]/.test(v) ? `"${v}"` : v;
    }).join(",")).join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "pro-irp-client-import-template.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  async function onImport() {
    try {
      setErr("");
      setMsg("");
      setImporting(true);

      // Build objects from mapping
      const out = rows.map((r) => {
        const o = {};
        ALL_FIELDS.forEach(({ key }) => {
          const i = map[key];
          if (i != null && i !== "") o[key] = r[i] ?? "";
        });

        // Handle Full Name field - split into first/last
        // Supports: "Last, First" (Anthem), "First Last" (UHC), "First Middle Last"
        if (o.fullName && o.fullName.trim()) {
          const fullName = String(o.fullName).trim();

          // Check for "Last, First" format (comma-separated - common for Anthem, Humana, etc.)
          if (fullName.includes(',')) {
            const [lastName, firstName] = fullName.split(',').map(s => s.trim());
            if (!o.lastName && lastName) o.lastName = lastName;
            if (!o.firstName && firstName) o.firstName = firstName;
          } else {
            // "First Last" or "First Middle Last" format (UHC, Aetna, etc.)
            const nameParts = fullName.split(/\s+/);
            if (nameParts.length === 1) {
              // Single name - use as first name
              if (!o.firstName) o.firstName = nameParts[0];
            } else if (nameParts.length === 2) {
              // First Last
              if (!o.firstName) o.firstName = nameParts[0];
              if (!o.lastName) o.lastName = nameParts[1];
            } else {
              // Multiple parts - first word is first name, rest is last name
              if (!o.firstName) o.firstName = nameParts[0];
              if (!o.lastName) o.lastName = nameParts.slice(1).join(" ");
            }
          }
          delete o.fullName; // Don't send fullName to backend
        }

        // Legacy fallback: Split "firstName" field if it contains full name
        if ((!o.lastName || !o.firstName) && o.firstName && !map.lastName && / /.test(o.firstName)) {
          const [first, ...rest] = String(o.firstName).split(" ");
          o.firstName = first;
          o.lastName = rest.join(" ");
        }

        // Validate and clean data
        if (o.effectiveDate) {
          o.effectiveDate = validateDate(o.effectiveDate);
        }
        if (o.terminationDate) {
          o.terminationDate = validateDate(o.terminationDate);
        }
        if (o.dob) {
          o.dob = validateDate(o.dob);
        }
        if (o.status) {
          o.status = validateStatus(o.status);
        }
        // Clean term reason (trim whitespace, normalize case)
        if (o.termReason) {
          o.termReason = String(o.termReason).trim();
        }

        // Apply default carrier if no carrier column mapped and default is set
        if (!o.carrier && defaultCarrier) {
          o.carrier = defaultCarrier;
        }

        // Auto-detect carrier from plan name if still no carrier
        // (handles files like Anthem where plan name starts with carrier)
        if (!o.carrier && o.plan) {
          const detected = detectCarrierFromPlan(o.plan);
          if (detected) {
            o.carrier = detected;
          }
        }

        return o;
      });

      // Separate clients with inactive/lost/churned status - these should be SKIPPED
      // Carrier exports often have "Inactive" for clients who left the carrier
      const skipped = [];
      const validStatuses = ['active', '', undefined, null];

      const cleaned = out.filter(o => {
        // Must have a name to import
        const hasName = String(o.firstName || "").trim() !== "" || String(o.lastName || "").trim() !== "";
        if (!hasName) {
          skipped.push({
            name: 'Unknown',
            reason: 'Missing name - cannot import client without a name'
          });
          return false;
        }

        const clientName = `${o.firstName || ''} ${o.lastName || ''}`.trim();

        // Check if status is inactive - uses user's classification from status modal
        const rawStatus = o.status || '';
        const statusLower = rawStatus.toLowerCase().trim();

        // Check user's classification first, then fall back to smart guess
        const isInactive = inactiveStatusSet.has(statusLower) ||
          (!statusValues.some(v => v.value.toLowerCase() === statusLower) && guessIfInactive(rawStatus));

        if (isInactive) {
          skipped.push({
            name: clientName,
            reason: `Status "${rawStatus}" = Inactive - client marked as inactive/lost in carrier export`
          });
          return false;
        }

        return true;
      });

      // Store skipped clients for breakdown display
      setSkippedClients(skipped);

      console.log('[Import] Cleaned rows:', cleaned.length, 'Skipped:', skipped.length, 'Sample:', cleaned[0]);

      // Filter out records that were marked as skipped in duplicate review
      const finalItems = [];

      for (let cleanedIdx = 0; cleanedIdx < cleaned.length; cleanedIdx++) {
        const item = cleaned[cleanedIdx];
        // Find the original index of this item in the 'out' array
        const originalIdx = out.findIndex(o =>
          o.firstName === item.firstName &&
          o.lastName === item.lastName &&
          o.phone === item.phone
        );

        // Skip items marked for skipping
        if (skippedDuplicates.has(originalIdx)) {
          continue;
        }

        // Add to final items
        finalItems.push(item);
      }

      console.log('[Import] After duplicate skips:', finalItems.length);

      // Send resolvedDuplicates directly - backend matches by clientId, not index
      // Each resolution contains { clientId, firstName, lastName, ... } so backend can match
      const resolutionsToSend = Object.keys(resolvedDuplicates).length > 0 ? resolvedDuplicates : null;
      console.log('[Import] Resolutions to send:', resolutionsToSend ? Object.keys(resolvedDuplicates).length : 0);
      if (resolutionsToSend) {
        console.log('[Import] Resolution clientIds:', Object.values(resolvedDuplicates).map(r => r.clientId));
      }

      if (finalItems.length === 0) {
        setErr("No valid clients to import. All records were skipped.");
        setImporting(false);
        return;
      }

      // Save mapping
      if (remember && headers.length) {
        localStorage.setItem(MAP_KEY, JSON.stringify({ ...map, __headers: headers }));
      }

      // Pass resolved duplicates to backend for proper merging
      const result = await bulkImportClients(
        finalItems,
        true,
        file?.name || 'import.csv',
        resolutionsToSend
      );

      // Mark selected clients as inactive (clients missing from carrier book)
      let markedInactiveCount = 0;
      if (clientsToMarkInactive.size > 0) {
        console.log('[Import] Marking', clientsToMarkInactive.size, 'clients as inactive...');
        for (const clientId of clientsToMarkInactive) {
          try {
            await updateClient(clientId, { status: 'inactive' });
            markedInactiveCount++;
          } catch (err) {
            console.error('[Import] Failed to mark client', clientId, 'as inactive:', err);
          }
        }
        console.log('[Import] Marked', markedInactiveCount, 'clients as inactive');
      }

      // Add inactive count to result
      setImportResult({
        ...result,
        markedInactive: markedInactiveCount
      });
      setStep(3);

    } catch (e) {
      setErr("Import failed: " + (e?.message || "Unknown error"));
    } finally {
      setImporting(false);
    }
  }

  // Helper to prepare import items (same logic as onImport uses)
  function prepareImportItems() {
    return rows.map((r) => {
      const o = {};
      ALL_FIELDS.forEach(({ key }) => {
        const i = map[key];
        if (i != null && i !== "") o[key] = r[i] ?? "";
      });

      // Handle Full Name field - split into first/last
      if (o.fullName && o.fullName.trim()) {
        const fullName = String(o.fullName).trim();
        if (fullName.includes(',')) {
          const [lastName, firstName] = fullName.split(',').map(s => s.trim());
          if (!o.lastName && lastName) o.lastName = lastName;
          if (!o.firstName && firstName) o.firstName = firstName;
        } else {
          const nameParts = fullName.split(/\s+/);
          if (nameParts.length === 1) {
            if (!o.firstName) o.firstName = nameParts[0];
          } else if (nameParts.length === 2) {
            if (!o.firstName) o.firstName = nameParts[0];
            if (!o.lastName) o.lastName = nameParts[1];
          } else {
            if (!o.firstName) o.firstName = nameParts[0];
            if (!o.lastName) o.lastName = nameParts.slice(1).join(" ");
          }
        }
        delete o.fullName;
      }

      // Legacy fallback
      if ((!o.lastName || !o.firstName) && o.firstName && !map.lastName && / /.test(o.firstName)) {
        const [first, ...rest] = String(o.firstName).split(" ");
        o.firstName = first;
        o.lastName = rest.join(" ");
      }

      // Validate dates
      if (o.effectiveDate) o.effectiveDate = validateDate(o.effectiveDate);
      if (o.terminationDate) o.terminationDate = validateDate(o.terminationDate);
      if (o.dob) o.dob = validateDate(o.dob);
      if (o.status) o.status = validateStatus(o.status);
      if (o.termReason) o.termReason = String(o.termReason).trim();

      // Apply default carrier
      if (!o.carrier && defaultCarrier) o.carrier = defaultCarrier;
      if (!o.carrier && o.plan) {
        const detected = detectCarrierFromPlan(o.plan);
        if (detected) o.carrier = detected;
      }

      return o;
    });
  }

  // Run duplicate check when moving to Step 2
  async function runDuplicateCheck() {
    setCheckingDuplicates(true);
    setPotentialDuplicates([]);
    setResolvedDuplicates({});
    setSkippedDuplicates(new Set());
    setPotentialInactives([]);
    setImportCarrier(null);
    setClientsToMarkInactive(new Set());
    setDismissedInactives(new Set());

    try {
      const items = prepareImportItems();
      const result = await checkDuplicates(items);

      if (result.ok && result.duplicates && result.duplicates.length > 0) {
        setPotentialDuplicates(result.duplicates);
        console.log('[Import] Found', result.duplicates.length, 'potential duplicates');
      }

      // Handle potential inactives (clients missing from this carrier's book)
      if (result.ok && result.potentialInactives && result.potentialInactives.length > 0) {
        setPotentialInactives(result.potentialInactives);
        setImportCarrier(result.importCarrier);
        console.log('[Import] Found', result.potentialInactives.length, 'potential inactives for carrier:', result.importCarrier);
      }
    } catch (error) {
      console.error('[Import] Duplicate check failed:', error);
      // Continue anyway - don't block import if duplicate check fails
    } finally {
      setCheckingDuplicates(false);
    }
  }

  // Handle duplicate modal actions
  function handleDuplicateSave(importIndex, mergedRecord) {
    setResolvedDuplicates(prev => ({ ...prev, [importIndex]: mergedRecord }));
    setDuplicateModalOpen(false);

    // Move to next unresolved duplicate
    const nextIndex = potentialDuplicates.findIndex((d, i) =>
      i > currentDuplicateIndex &&
      !resolvedDuplicates[d.importIndex] &&
      !skippedDuplicates.has(d.importIndex)
    );
    if (nextIndex >= 0) {
      setCurrentDuplicateIndex(nextIndex);
      setDuplicateModalOpen(true);
    }
  }

  function handleDuplicateSkip(importIndex) {
    setSkippedDuplicates(prev => new Set([...prev, importIndex]));
    setDuplicateModalOpen(false);

    // Move to next unresolved duplicate
    const nextIndex = potentialDuplicates.findIndex((d, i) =>
      i > currentDuplicateIndex &&
      !resolvedDuplicates[d.importIndex] &&
      !skippedDuplicates.has(d.importIndex)
    );
    if (nextIndex >= 0) {
      setCurrentDuplicateIndex(nextIndex);
      setDuplicateModalOpen(true);
    }
  }

  function openDuplicateModal(index) {
    setCurrentDuplicateIndex(index);
    setDuplicateModalOpen(true);
  }

  // Count unresolved duplicates
  const unresolvedDuplicateCount = useMemo(() => {
    return potentialDuplicates.filter(d =>
      !resolvedDuplicates[d.importIndex] && !skippedDuplicates.has(d.importIndex)
    ).length;
  }, [potentialDuplicates, resolvedDuplicates, skippedDuplicates]);

  async function goToStep(newStep) {
    if (newStep === 2 && step === 1) {
      // Validate before moving to review
      if (!validateMapping()) {
        return;
      }
      // Run duplicate check when entering Step 2
      setStep(newStep);
      runDuplicateCheck();
      return;
    }
    setStep(newStep);
  }

  const preview = useMemo(() => rows.slice(0, 5), [rows]);
  const mappedCount = Object.values(map).filter(v => v != null && v !== "").length;
  const hasEffectiveDate = map.effectiveDate != null;
  const hasName = map.fullName != null || map.firstName != null || map.lastName != null;
  const hasStatus = map.status != null;

  // Build set of inactive status values from user classification
  const inactiveStatusSet = useMemo(() => {
    const set = new Set();
    statusValues.filter(v => v.isInactive).forEach(v => set.add(v.value.toLowerCase()));
    return set;
  }, [statusValues]);

  // Pre-calculate inactive clients that will be skipped
  // Uses user-classified statusValues instead of hardcoded patterns
  const isInactiveStatus = (val) => {
    const status = String(val || '').trim();
    // Check user's classification first
    if (inactiveStatusSet.has(status.toLowerCase())) return true;
    // If no classification exists for this value, fall back to smart guess
    const hasClassification = statusValues.some(v => v.value.toLowerCase() === status.toLowerCase());
    if (!hasClassification) return guessIfInactive(status);
    return false;
  };

  const inactivePreview = useMemo(() => {
    if (!hasStatus) return { count: 0, samples: [] };

    const statusIdx = map.status;
    const inactiveRows = rows.filter(row => isInactiveStatus(row[statusIdx]));

    // Get first 5 names for preview
    const samples = inactiveRows.slice(0, 5).map(row => {
      const fullName = map.fullName != null ? row[map.fullName] : '';
      const firstName = map.firstName != null ? row[map.firstName] : '';
      const lastName = map.lastName != null ? row[map.lastName] : '';

      if (fullName) return fullName;
      return `${firstName} ${lastName}`.trim() || 'Unknown';
    });

    return { count: inactiveRows.length, samples };
  }, [rows, map, hasStatus]);

  // Build preview data
  const previewData = useMemo(() => {
    return preview.map(row => {
      const obj = {};
      ALL_FIELDS.forEach(({ key }) => {
        const i = map[key];
        if (i != null) obj[key] = row[i] || '';
      });

      // Handle fullName in preview - split for display
      // Supports: "Last, First" (Anthem), "First Last" (UHC), "First Middle Last"
      if (obj.fullName && obj.fullName.trim()) {
        const fullName = String(obj.fullName).trim();

        if (fullName.includes(',')) {
          // "Last, First" format (Anthem, Humana, etc.)
          const [lastName, firstName] = fullName.split(',').map(s => s.trim());
          if (!obj.lastName && lastName) obj.lastName = lastName;
          if (!obj.firstName && firstName) obj.firstName = firstName;
        } else {
          // "First Last" format
          const nameParts = fullName.split(/\s+/);
          if (nameParts.length === 1) {
            if (!obj.firstName) obj.firstName = nameParts[0];
          } else if (nameParts.length === 2) {
            if (!obj.firstName) obj.firstName = nameParts[0];
            if (!obj.lastName) obj.lastName = nameParts[1];
          } else {
            if (!obj.firstName) obj.firstName = nameParts[0];
            if (!obj.lastName) obj.lastName = nameParts.slice(1).join(" ");
          }
        }
      }

      // Apply default carrier for preview
      if (!obj.carrier && defaultCarrier) {
        obj.carrier = defaultCarrier;
      }

      // Auto-detect carrier from plan name for preview
      if (!obj.carrier && obj.plan) {
        const detected = detectCarrierFromPlan(obj.plan);
        if (detected) {
          obj.carrier = detected;
        }
      }

      return obj;
    });
  }, [preview, map, defaultCarrier]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 py-8">
      <div className="max-w-5xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-6" data-tour="import-header">
          <div className="flex items-center justify-center gap-3 mb-2">
            <Users className="w-8 h-8 text-indigo-600" />
            <h1 className="text-3xl font-bold text-slate-800">Import Clients</h1>
          </div>
          <p className="text-slate-600">
            Easily import your client list in just a few steps
          </p>
        </div>

        {/* Step Indicator */}
        <div data-tour="import-step-indicator">
          <StepIndicator currentStep={step} steps={steps} />
        </div>

        {/* Error Message */}
        {err && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-start gap-3">
            <AlertCircle className="mt-0.5 flex-shrink-0" size={20} />
            <div>{err}</div>
            <button onClick={() => setErr("")} className="ml-auto text-red-500 hover:text-red-700">
              <X size={18} />
            </button>
          </div>
        )}

        {/* Success Message */}
        {msg && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl flex items-start gap-3">
            <CheckCircle className="mt-0.5 flex-shrink-0" size={20} />
            <div>{msg}</div>
            <button onClick={() => setMsg("")} className="ml-auto text-green-500 hover:text-green-700">
              <X size={18} />
            </button>
          </div>
        )}

        {/* Recent Imports Section - Collapsible */}
        <div className="mb-6">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-800 transition mb-3"
          >
            <History size={18} />
            {showHistory ? 'Hide' : 'View'} Recent Imports
            {showHistory ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>

          {showHistory && (
            <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                  <History size={18} className="text-indigo-500" />
                  Recent Bulk Imports
                </h3>
                <button
                  onClick={loadImportHistory}
                  disabled={loadingHistory}
                  className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                >
                  {loadingHistory ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
                  Refresh
                </button>
              </div>

              {loadingHistory ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 text-indigo-500 animate-spin" />
                </div>
              ) : importHistory.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Clock size={32} className="mx-auto mb-2 text-slate-300" />
                  <p>No import history found</p>
                  <p className="text-sm">Your import batches will appear here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {importHistory.map((imp) => {
                    const isReversed = imp.status === 'reversed';
                    // Support both camelCase (API) and snake_case (legacy) property names
                    const createdAt = imp.createdAt || imp.created_at;
                    const createdCount = imp.createdCount ?? imp.created_count ?? 0;
                    const skippedCount = imp.skippedCount ?? imp.skipped_count ?? 0;
                    const errorCount = imp.errorCount ?? imp.error_count ?? 0;
                    const reversedAt = imp.reversedAt || imp.reversed_at;
                    const reversedCount = imp.reversedCount ?? imp.reversed_count ?? 0;

                    const date = new Date(createdAt);
                    const formattedDate = date.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit'
                    });

                    return (
                      <div
                        key={imp.id}
                        className={`p-4 rounded-lg border ${
                          isReversed
                            ? 'bg-gray-50 border-gray-200'
                            : 'bg-white border-gray-200 hover:border-indigo-200'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-slate-800">
                                {imp.filename || 'Unnamed Import'}
                              </span>
                              <span className={`px-2 py-0.5 rounded-full text-xs ${
                                isReversed
                                  ? 'bg-gray-200 text-gray-600'
                                  : 'bg-green-100 text-green-700'
                              }`}>
                                {imp.status || 'complete'}
                              </span>
                            </div>
                            <div className="text-sm text-slate-500 flex items-center gap-3">
                              <span className="flex items-center gap-1">
                                <Clock size={14} />
                                {formattedDate}
                              </span>
                              <span>•</span>
                              <span className="text-green-600">{createdCount} added</span>
                              {skippedCount > 0 && (
                                <>
                                  <span>•</span>
                                  <span className="text-yellow-600">{skippedCount} skipped</span>
                                </>
                              )}
                              {errorCount > 0 && (
                                <>
                                  <span>•</span>
                                  <span className="text-red-600">{errorCount} errors</span>
                                </>
                              )}
                            </div>
                            {isReversed && reversedAt && (
                              <div className="text-xs text-gray-500 mt-1">
                                Reversed on {new Date(reversedAt).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric'
                                })} ({reversedCount} clients removed)
                              </div>
                            )}
                          </div>

                          {!isReversed && (
                            <button
                              onClick={() => handleReverseImport(imp.id)}
                              disabled={reversingId === imp.id}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm bg-red-50 text-red-600 hover:bg-red-100 transition border border-red-200 disabled:opacity-50"
                            >
                              {reversingId === imp.id ? (
                                <Loader2 size={14} className="animate-spin" />
                              ) : (
                                <RotateCcw size={14} />
                              )}
                              Undo
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* STEP 0: Upload File */}
        {step === 0 && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-slate-800 mb-2">Step 1: Upload Your Client List</h2>
              <p className="text-slate-500">Upload a CSV or Excel file with your client data</p>
            </div>

            {/* Download Template Button */}
            <div className="flex justify-center mb-6" data-tour="import-template-download">
              <button
                onClick={downloadTemplate}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition border border-indigo-200"
              >
                <Download size={18} />
                Download Sample Template
              </button>
            </div>

            {/* Drag and Drop Zone */}
            <div
              data-tour="import-upload-zone"
              className={`relative border-2 border-dashed rounded-2xl p-12 transition-all cursor-pointer ${
                isDragging
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-gray-300 hover:border-indigo-400 hover:bg-gray-50'
              }`}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                onChange={onFile}
                className="hidden"
              />

              <div className="flex flex-col items-center">
                <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 transition-all ${
                  isDragging ? 'bg-indigo-100' : 'bg-gray-100'
                }`}>
                  <FileSpreadsheet className={`w-10 h-10 ${isDragging ? 'text-indigo-600' : 'text-gray-400'}`} />
                </div>

                <div className="text-lg font-medium text-slate-700 mb-1">
                  {isDragging ? 'Drop your file here!' : 'Drag & drop your CSV or Excel file here'}
                </div>
                <div className="text-sm text-slate-500 mb-4">or click to browse</div>

                <div className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition">
                  Select File
                </div>
              </div>
            </div>

            {/* Tips */}
            <div className="mt-8 bg-blue-50 rounded-xl p-4 border border-blue-100" data-tour="import-tips">
              <div className="flex items-start gap-3">
                <Info className="text-blue-600 mt-0.5 flex-shrink-0" size={20} />
                <div className="text-sm text-blue-900">
                  <div className="font-semibold mb-2">Tips for a smooth import:</div>
                  <ul className="space-y-1 list-disc list-inside text-blue-800">
                    <li>Make sure your file has a <b>header row</b> with column names</li>
                    <li>Include <b>Effective Date</b> for retention tracking features</li>
                    <li>Include <b>Email</b> to avoid duplicate imports</li>
                    <li>Dates should be in MM/DD/YYYY format</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* STEP 1: Map Columns */}
        {step === 1 && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-slate-800 mb-2">Step 2: Map Your Columns</h2>
              <p className="text-slate-500">
                Tell us which columns in your file match our fields
                {mappedCount > 0 && <span className="text-green-600 ml-2">({mappedCount} auto-mapped!)</span>}
              </p>
            </div>

            {/* File Info */}
            <div className="mb-6 p-4 bg-gray-50 rounded-xl flex items-center gap-4">
              <FileCheck className="text-green-600" size={24} />
              <div>
                <div className="font-medium text-slate-800">{file?.name}</div>
                <div className="text-sm text-slate-500">{rows.length} rows found • {headers.length} columns</div>
              </div>
              <button
                onClick={() => { setStep(0); setFile(null); setHeaders([]); setRows([]); }}
                className="ml-auto text-sm text-indigo-600 hover:underline"
              >
                Choose different file
              </button>
            </div>

            {/* Auto-mapped indicator */}
            {mappedCount > 0 && (
              <div className="mb-6 p-3 bg-green-50 rounded-lg border border-green-200 flex items-center gap-2">
                <Sparkles className="text-green-600" size={18} />
                <span className="text-green-800 text-sm font-medium">
                  We automatically mapped {mappedCount} fields! Review them below.
                </span>
              </div>
            )}

            {/* Effective Date missing warning — shown before Required Fields */}
            {!hasEffectiveDate && (
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="text-amber-600 mt-0.5 flex-shrink-0" size={18} />
                  <div className="text-sm text-amber-800">
                    <span className="font-semibold">Map "Effective Date" to unlock retention features.</span>{' '}
                    Without it, Pro IRP cannot calculate when policies renew, identify at-risk clients, or build your OEP cohort. It's the most important column in your carrier export — look for columns like "Eff Date", "Plan Start", or "Enrollment Date".
                  </div>
                </div>
              </div>
            )}

            {/* Required Fields */}
            <div className="mb-6">
              <h3 className="flex items-center gap-2 text-sm font-bold text-red-600 mb-3">
                <Shield size={16} /> Required Fields
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {FIELD_GROUPS.required.map(({ key, label, desc }) => (
                  <MappingField
                    key={key}
                    field={key}
                    label={label}
                    desc={desc}
                    headers={headers}
                    value={map[key]}
                    onChange={onChangeMap}
                    required
                  />
                ))}
              </div>
            </div>

            {/* Critical Fields */}
            <div className="mb-6">
              <h3 className="flex items-center gap-2 text-sm font-bold text-amber-600 mb-3">
                <AlertTriangle size={16} /> Critical for Retention Tracking
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {FIELD_GROUPS.policy.filter(f => f.critical).map(({ key, label, desc }) => (
                  <MappingField
                    key={key}
                    field={key}
                    label={label}
                    desc={desc}
                    headers={headers}
                    value={map[key]}
                    onChange={onChangeMap}
                    critical
                  />
                ))}
                {FIELD_GROUPS.contact.map(({ key, label, desc }) => (
                  <MappingField
                    key={key}
                    field={key}
                    label={label}
                    desc={desc}
                    headers={headers}
                    value={map[key]}
                    onChange={onChangeMap}
                  />
                ))}
              </div>
            </div>

            {/* Default Carrier - Show if no carrier column is mapped */}
            {map.carrier == null && (
              <div className="mb-6 p-4 bg-indigo-50 rounded-xl border border-indigo-200">
                <div className="flex items-start gap-3">
                  <Info className="text-indigo-600 mt-0.5 flex-shrink-0" size={20} />
                  <div className="flex-1">
                    <h4 className="font-semibold text-indigo-900 mb-2">
                      No Carrier Column Found
                    </h4>
                    <p className="text-sm text-indigo-800 mb-3">
                      Is this entire book from one carrier? Select a default carrier to apply to all imported clients:
                    </p>
                    <CarrierSearchDropdown
                      carriers={CARRIERS}
                      value={defaultCarrier}
                      onChange={setDefaultCarrier}
                      placeholder="Search for a carrier..."
                    />
                    {defaultCarrier && (
                      <p className="text-xs text-indigo-600 mt-1 flex items-center gap-1">
                        <Check size={14} /> All clients will be assigned to <b>{defaultCarrier}</b>
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Show More Fields */}
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800 mb-4"
            >
              {showAdvanced ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              {showAdvanced ? 'Hide' : 'Show'} additional fields ({FIELD_GROUPS.policy.filter(f => !f.critical).length + FIELD_GROUPS.personal.length + FIELD_GROUPS.other.length} more)
            </button>

            {showAdvanced && (
              <>
                {/* Policy Fields */}
                <div className="mb-6">
                  <h3 className="text-sm font-bold text-slate-600 mb-3">Policy Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {FIELD_GROUPS.policy.filter(f => !f.critical).map(({ key, label, desc }) => (
                      <MappingField
                        key={key}
                        field={key}
                        label={label}
                        desc={desc}
                        headers={headers}
                        value={map[key]}
                        onChange={onChangeMap}
                      />
                    ))}
                  </div>
                </div>

                {/* Personal Fields */}
                <div className="mb-6">
                  <h3 className="text-sm font-bold text-slate-600 mb-3">Personal Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {FIELD_GROUPS.personal.map(({ key, label, desc }) => (
                      <MappingField
                        key={key}
                        field={key}
                        label={label}
                        desc={desc}
                        headers={headers}
                        value={map[key]}
                        onChange={onChangeMap}
                      />
                    ))}
                  </div>
                </div>

                {/* Other Fields */}
                <div className="mb-6">
                  <h3 className="text-sm font-bold text-slate-600 mb-3">Other</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {FIELD_GROUPS.other.map(({ key, label, desc }) => (
                      <MappingField
                        key={key}
                        field={key}
                        label={label}
                        desc={desc}
                        headers={headers}
                        value={map[key]}
                        onChange={onChangeMap}
                      />
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Inline status classifier prompt — shown when status column is mapped */}
            {statusValues.length > 0 && (
              <div className="mt-6 p-4 bg-indigo-50 border border-indigo-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <AlertCircle className="text-indigo-600 mt-0.5 flex-shrink-0" size={18} />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-indigo-900 mb-1">
                      We found {statusValues.length} status value{statusValues.length !== 1 ? 's' : ''} in your file — review which ones mean "inactive"
                    </p>
                    <p className="text-xs text-indigo-700 mb-2">
                      Clients marked inactive will be skipped during import.
                      {inactiveStatusSet.size > 0 && (
                        <span className="font-medium"> Currently {inactiveStatusSet.size} value{inactiveStatusSet.size !== 1 ? 's' : ''} marked as inactive.</span>
                      )}
                    </p>
                    <button
                      type="button"
                      onClick={() => setShowStatusModal(true)}
                      className="text-xs font-medium text-indigo-700 underline hover:text-indigo-900"
                    >
                      Review status values →
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Remember mapping */}
            <label className="flex items-center gap-2 text-sm text-slate-600 mt-4">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="rounded"
              />
              Remember this mapping for future imports
            </label>

            {/* Navigation */}
            <div className="flex justify-between mt-8 pt-6 border-t">
              <button
                onClick={() => setStep(0)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition"
              >
                <ArrowLeft size={18} />
                Back
              </button>
              <button
                onClick={() => goToStep(2)}
                disabled={!hasName}
                className="flex items-center gap-2 px-6 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Review Data
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: Review Data */}
        {step === 2 && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-slate-800 mb-2">Step 3: Review Your Data</h2>
              <p className="text-slate-500">Make sure everything looks correct before importing</p>
            </div>

            {/* Validation Issues */}
            {validationIssues.length > 0 && (
              <div className="mb-6 space-y-2">
                {validationIssues.map((issue, idx) => (
                  <div key={idx} className={`p-3 rounded-lg flex items-start gap-2 ${
                    issue.type === 'error'
                      ? 'bg-red-50 border border-red-200 text-red-800'
                      : 'bg-yellow-50 border border-yellow-200 text-yellow-800'
                  }`}>
                    {issue.type === 'error' ? <X size={18} /> : <AlertTriangle size={18} />}
                    <span className="text-sm">{issue.message}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Summary */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-indigo-50 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-indigo-600">{rows.length - inactivePreview.count}</div>
                <div className="text-sm text-indigo-800">Will Import</div>
              </div>
              <div className="bg-green-50 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-green-600">{mappedCount}</div>
                <div className="text-sm text-green-800">Fields Mapped</div>
              </div>
              {inactivePreview.count > 0 ? (
                <div className="bg-yellow-50 rounded-xl p-4 text-center">
                  <div className="text-3xl font-bold text-yellow-600">{inactivePreview.count}</div>
                  <div className="text-sm text-yellow-800">Will Skip (Inactive)</div>
                </div>
              ) : (
                <div className={`rounded-xl p-4 text-center ${hasEffectiveDate ? 'bg-green-50' : 'bg-yellow-50'}`}>
                  <div className={`text-3xl font-bold ${hasEffectiveDate ? 'text-green-600' : 'text-yellow-600'}`}>
                    {hasEffectiveDate ? <Check size={32} className="mx-auto" /> : <AlertTriangle size={32} className="mx-auto" />}
                  </div>
                  <div className={`text-sm ${hasEffectiveDate ? 'text-green-800' : 'text-yellow-800'}`}>
                    {hasEffectiveDate ? 'Retention Ready' : 'No Effective Date'}
                  </div>
                </div>
              )}
            </div>

            {/* Inactive Clients Warning */}
            {inactivePreview.count > 0 && (
              <div className="mb-6 p-4 bg-yellow-50 rounded-xl border border-yellow-200">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="text-yellow-600 mt-0.5 flex-shrink-0" size={20} />
                  <div className="flex-1">
                    <h4 className="font-semibold text-yellow-900 mb-1">
                      {inactivePreview.count} Inactive Client{inactivePreview.count > 1 ? 's' : ''} Will Be Skipped
                    </h4>
                    <p className="text-sm text-yellow-800 mb-2">
                      These clients have "Inactive" or "Lost" status in your carrier export and won't be imported.
                      This is typically because they've left this carrier.
                    </p>
                    <div className="text-sm text-yellow-700">
                      <span className="font-medium">Examples: </span>
                      {inactivePreview.samples.join(', ')}
                      {inactivePreview.count > 5 && ` and ${inactivePreview.count - 5} more...`}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Duplicate Detection Section */}
            {checkingDuplicates && (
              <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
                <div className="flex items-center gap-3">
                  <Loader2 className="text-blue-600 animate-spin" size={20} />
                  <div className="text-blue-800">
                    <span className="font-medium">Checking for duplicates...</span>
                    <span className="text-sm ml-2">Comparing against existing clients</span>
                  </div>
                </div>
              </div>
            )}

            {!checkingDuplicates && potentialDuplicates.length > 0 && (
              <div className="mb-6 p-4 bg-amber-50 rounded-xl border border-amber-200">
                <div className="flex items-start gap-3">
                  <Users className="text-amber-600 mt-0.5 flex-shrink-0" size={20} />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-amber-900">
                        {potentialDuplicates.length} Potential Duplicate{potentialDuplicates.length > 1 ? 's' : ''} Found
                      </h4>
                      {unresolvedDuplicateCount > 0 && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                          {unresolvedDuplicateCount} unresolved
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-amber-800 mb-3">
                      These clients may already exist in your database. Review each one to decide which field values to keep.
                    </p>

                    {/* Duplicate List */}
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {potentialDuplicates.map((dup, idx) => {
                        const isResolved = resolvedDuplicates[dup.importIndex] !== undefined;
                        const isSkipped = skippedDuplicates.has(dup.importIndex);
                        const status = isResolved ? 'resolved' : isSkipped ? 'skipped' : 'pending';

                        return (
                          <div
                            key={idx}
                            onClick={() => !isResolved && !isSkipped && openDuplicateModal(idx)}
                            className={`p-3 rounded-lg border cursor-pointer transition ${
                              status === 'resolved'
                                ? 'bg-green-50 border-green-200'
                                : status === 'skipped'
                                ? 'bg-gray-50 border-gray-200'
                                : 'bg-white border-amber-200 hover:border-amber-400'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="font-medium text-slate-800">
                                  {dup.importingRecord.firstName} {dup.importingRecord.lastName}
                                </span>
                                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                                  dup.matchType === 'medicare_id' || dup.matchType === 'ssn'
                                    ? 'bg-purple-100 text-purple-700'
                                    : dup.matchType === 'name_phone' || dup.matchType === 'phone_only'
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-blue-100 text-blue-700'
                                }`}>
                                  {dup.matchTypeLabel}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                {status === 'resolved' && (
                                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 flex items-center gap-1">
                                    <Check size={12} /> Merged
                                  </span>
                                )}
                                {status === 'skipped' && (
                                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-600">
                                    Skipped
                                  </span>
                                )}
                                {status === 'pending' && (
                                  <ArrowRight size={16} className="text-amber-600" />
                                )}
                              </div>
                            </div>
                            <div className="text-xs text-slate-500 mt-1">
                              Existing: {dup.existingClient.firstName} {dup.existingClient.lastName}
                              {dup.existingClient.carrier && ` - ${dup.existingClient.carrier}`}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Potential Inactives - Clients missing from re-imported book */}
            {!checkingDuplicates && potentialInactives.length > 0 && (
              <div className="mb-6 p-4 bg-red-50 rounded-xl border border-red-200">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="text-red-600 mt-0.5 flex-shrink-0" size={20} />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-red-900">
                        {potentialInactives.length} Client{potentialInactives.length > 1 ? 's' : ''} Missing from {importCarrier} Book
                      </h4>
                      <div className="flex items-center gap-2">
                        {clientsToMarkInactive.size > 0 && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                            {clientsToMarkInactive.size} to mark inactive
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-red-800 mb-3">
                      These clients are currently active with {importCarrier} but are <strong>not in the new book</strong>.
                      They may have left or switched carriers. Select which ones to mark as inactive.
                    </p>

                    {/* Quick Actions */}
                    <div className="flex gap-2 mb-3">
                      <button
                        onClick={() => {
                          const allIds = potentialInactives
                            .filter(c => !dismissedInactives.has(c.id))
                            .map(c => c.id);
                          setClientsToMarkInactive(new Set(allIds));
                        }}
                        className="px-3 py-1.5 text-xs rounded-lg bg-red-100 text-red-700 hover:bg-red-200 transition font-medium"
                      >
                        Select All ({potentialInactives.length - dismissedInactives.size})
                      </button>
                      <button
                        onClick={() => setClientsToMarkInactive(new Set())}
                        className="px-3 py-1.5 text-xs rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition"
                      >
                        Clear Selection
                      </button>
                      <button
                        onClick={() => {
                          const allIds = potentialInactives.map(c => c.id);
                          setDismissedInactives(new Set(allIds));
                          setClientsToMarkInactive(new Set());
                        }}
                        className="px-3 py-1.5 text-xs rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition"
                      >
                        Dismiss All
                      </button>
                    </div>

                    {/* Potential Inactives List */}
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {potentialInactives
                        .filter(client => !dismissedInactives.has(client.id))
                        .map((client) => {
                          const isSelected = clientsToMarkInactive.has(client.id);

                          return (
                            <div
                              key={client.id}
                              onClick={() => {
                                setClientsToMarkInactive(prev => {
                                  const newSet = new Set(prev);
                                  if (newSet.has(client.id)) {
                                    newSet.delete(client.id);
                                  } else {
                                    newSet.add(client.id);
                                  }
                                  return newSet;
                                });
                              }}
                              className={`p-3 rounded-lg border cursor-pointer transition ${
                                isSelected
                                  ? 'bg-red-100 border-red-300'
                                  : 'bg-white border-red-200 hover:border-red-400'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => {}}
                                    className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500"
                                  />
                                  <div>
                                    <span className="font-medium text-slate-800">
                                      {client.firstName} {client.lastName}
                                    </span>
                                    <span className="ml-2 text-xs text-slate-500">
                                      {client.phone || client.email || ''}
                                    </span>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                    {client.plan || client.planType || 'No plan info'}
                                  </span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setDismissedInactives(prev => new Set([...prev, client.id]));
                                      setClientsToMarkInactive(prev => {
                                        const newSet = new Set(prev);
                                        newSet.delete(client.id);
                                        return newSet;
                                      });
                                    }}
                                    className="p-1 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600"
                                    title="Dismiss (keep as active)"
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              </div>
                              {client.effectiveDate && (
                                <div className="text-xs text-slate-500 mt-1 ml-7">
                                  Effective: {new Date(client.effectiveDate).toLocaleDateString()}
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>

                    {dismissedInactives.size > 0 && (
                      <div className="mt-3 text-xs text-gray-500">
                        {dismissedInactives.size} client{dismissedInactives.size > 1 ? 's' : ''} dismissed.{' '}
                        <button
                          onClick={() => setDismissedInactives(new Set())}
                          className="text-blue-600 hover:underline"
                        >
                          Undo
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Preview Table */}
            <div className="mb-6">
              <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                <Eye size={16} /> Preview (First 5 Rows)
              </h3>
              <div className="overflow-auto border rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-3 py-2 text-left border-b font-medium text-slate-600">Name</th>
                      <th className="px-3 py-2 text-left border-b font-medium text-slate-600">Contact</th>
                      <th className="px-3 py-2 text-left border-b font-medium text-slate-600">Effective Date</th>
                      <th className="px-3 py-2 text-left border-b font-medium text-slate-600">Carrier</th>
                      <th className="px-3 py-2 text-left border-b font-medium text-slate-600">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.map((row, idx) => (
                      <tr key={idx} className="odd:bg-white even:bg-slate-50/50">
                        <td className="px-3 py-2 border-b">
                          <span className="font-medium">{row.firstName} {row.lastName}</span>
                        </td>
                        <td className="px-3 py-2 border-b text-slate-600">
                          {row.email || row.phone || <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-3 py-2 border-b">
                          {row.effectiveDate || <span className="text-yellow-600">Not provided</span>}
                        </td>
                        <td className="px-3 py-2 border-b text-slate-600">
                          {row.carrier ? (
                            <span className={defaultCarrier && row.carrier === defaultCarrier && map.carrier == null ? 'text-indigo-600 font-medium' : ''}>
                              {row.carrier}
                              {defaultCarrier && row.carrier === defaultCarrier && map.carrier == null && (
                                <span className="text-xs text-indigo-400 ml-1">(default)</span>
                              )}
                            </span>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 border-b">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${
                            row.status === 'active' ? 'bg-green-100 text-green-800' :
                            row.status === 'inactive' ? 'bg-gray-100 text-gray-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {row.status || 'active'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {rows.length > 5 && (
                <div className="text-center text-sm text-slate-500 mt-2">
                  ...and {rows.length - 5} more rows
                </div>
              )}
            </div>

            {/* Duplicate Handling Info */}
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
              <div className="flex items-start gap-3">
                <Info className="text-blue-600 mt-0.5 flex-shrink-0" size={18} />
                <div className="text-sm text-blue-900">
                  <b>Duplicate handling:</b> If a client with the same <u>phone number</u> or <u>email</u> already exists, their record will be updated with the new data instead of creating a duplicate. Phone number is checked first since not every client has an email.
                </div>
              </div>
            </div>

            {/* Unresolved Duplicates Warning */}
            {unresolvedDuplicateCount > 0 && (
              <div className="mb-4 p-3 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-center gap-2 text-red-800">
                  <AlertTriangle size={18} />
                  <span className="text-sm">
                    <strong>{unresolvedDuplicateCount} duplicate{unresolvedDuplicateCount > 1 ? 's' : ''}</strong> need review before importing.
                    Click on each duplicate above to resolve.
                  </span>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between pt-6 border-t">
              <button
                onClick={() => setStep(1)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition"
              >
                <ArrowLeft size={18} />
                Back to Mapping
              </button>
              <button
                onClick={onImport}
                disabled={importing || validationIssues.some(i => i.type === 'error') || checkingDuplicates}
                className={`flex items-center gap-2 px-8 py-3 rounded-lg text-white transition disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-lg ${
                  unresolvedDuplicateCount > 0
                    ? 'bg-amber-600 hover:bg-amber-700'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {importing ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    Importing...
                  </>
                ) : checkingDuplicates ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    Checking duplicates...
                  </>
                ) : unresolvedDuplicateCount > 0 ? (
                  <>
                    <AlertTriangle size={20} />
                    Import Anyway ({rows.length - inactivePreview.count - skippedDuplicates.size})
                  </>
                ) : (
                  <>
                    <Upload size={20} />
                    Import {rows.length - inactivePreview.count - skippedDuplicates.size} Clients
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: Success */}
        {step === 3 && importResult && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>

            <h2 className="text-2xl font-bold text-slate-800 mb-2">Import Complete!</h2>
            <p className="text-slate-600 mb-8">Your clients have been successfully imported</p>

            {/* Results */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto mb-8">
              <div className="bg-green-50 rounded-xl p-4">
                <div className="text-3xl font-bold text-green-600">{importResult.added}</div>
                <div className="text-sm text-green-800">New Clients</div>
              </div>
              <div className="bg-purple-50 rounded-xl p-4">
                <div className="text-3xl font-bold text-purple-600">{importResult.merged || importResult.updated || 0}</div>
                <div className="text-sm text-purple-800">Updated</div>
              </div>
              {importResult.planSwitches > 0 && (
                <div className="bg-blue-50 rounded-xl p-4">
                  <div className="text-3xl font-bold text-blue-600">{importResult.planSwitches}</div>
                  <div className="text-sm text-blue-800">Plan Switches</div>
                </div>
              )}
              {importResult.markedInactive > 0 && (
                <div className="bg-red-50 rounded-xl p-4">
                  <div className="text-3xl font-bold text-red-600">{importResult.markedInactive}</div>
                  <div className="text-sm text-red-800">Marked Inactive</div>
                </div>
              )}
              <div
                className={`bg-gray-50 rounded-xl p-4 ${skippedClients.length > 0 ? 'cursor-pointer hover:bg-gray-100 transition' : ''}`}
                onClick={() => skippedClients.length > 0 && setShowSkippedModal(true)}
              >
                <div className="text-3xl font-bold text-gray-600">
                  {importResult.skipped + skippedClients.length}
                </div>
                <div className="text-sm text-gray-800">
                  Skipped
                  {skippedClients.length > 0 && (
                    <span className="text-indigo-600 ml-1">(click for details)</span>
                  )}
                </div>
              </div>
            </div>

            {/* Plan switch explanation */}
            {importResult.planSwitches > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 max-w-lg mx-auto mb-4">
                <p className="text-sm text-blue-800">
                  <strong>{importResult.planSwitches} client(s) are switching plans!</strong> We detected a future effective date
                  and saved their new plan info. These clients will be added to the OEP Hub for Jan 1st outreach.
                </p>
              </div>
            )}

            {/* Marked inactive explanation */}
            {importResult.markedInactive > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 max-w-lg mx-auto mb-4">
                <p className="text-sm text-red-800">
                  <strong>{importResult.markedInactive} client(s) marked as inactive.</strong> These clients were not found in the
                  new carrier book and have been flagged for follow-up. They may have switched carriers or disenrolled.
                </p>
              </div>
            )}

            {/* Merge explanation */}
            {(importResult.merged > 0 || importResult.updated > 0) && importResult.added === 0 && !importResult.planSwitches && (
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 max-w-lg mx-auto mb-8">
                <p className="text-sm text-purple-800">
                  <strong>All clients matched existing records.</strong> Their profiles have been merged with plan history tracking.
                  Future plans will automatically become active on their effective date.
                </p>
              </div>
            )}

            {/* Next Steps */}
            <div className="bg-indigo-50 rounded-xl p-6 text-left max-w-lg mx-auto mb-8">
              <h3 className="font-semibold text-indigo-900 mb-3">What's Next?</h3>
              <ul className="space-y-2 text-sm text-indigo-800">
                <li className="flex items-start gap-2">
                  <Check size={16} className="text-indigo-600 mt-0.5" />
                  View your updated client list
                </li>
                <li className="flex items-start gap-2">
                  <Check size={16} className="text-indigo-600 mt-0.5" />
                  Check the Dashboard for retention insights
                </li>
                <li className="flex items-start gap-2">
                  <Check size={16} className="text-indigo-600 mt-0.5" />
                  Set up OEP automations for your cohort
                </li>
              </ul>
            </div>

            <div className="flex gap-4 justify-center">
              <button
                onClick={() => nav("/clients")}
                className="px-6 py-3 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition font-semibold"
              >
                View Clients
              </button>
              <button
                onClick={() => nav("/dashboard")}
                className="px-6 py-3 rounded-lg border border-gray-300 hover:bg-gray-50 transition"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        )}

        {/* Skipped Clients Modal */}
        {/* Status Classification Modal - shown when status column is mapped */}
        {showStatusModal && statusValues.length > 0 && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-xl w-full max-h-[80vh] overflow-hidden">
              <div className="px-6 py-4 border-b bg-indigo-50 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <AlertTriangle className="text-indigo-600" size={20} />
                  Classify Status Values
                </h3>
                <button
                  onClick={() => setShowStatusModal(false)}
                  className="p-1 hover:bg-indigo-100 rounded-lg transition"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                <p className="text-sm text-slate-600 mb-4">
                  We found these status values in your file. <b>Check the ones that mean "Inactive"</b> -
                  clients with these statuses will be skipped during import.
                </p>
                <div className="space-y-2">
                  {statusValues.map((sv, idx) => (
                    <label
                      key={idx}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${
                        sv.isInactive
                          ? 'bg-red-50 border-red-200'
                          : 'bg-green-50 border-green-200 hover:bg-green-100'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={sv.isInactive}
                        onChange={(e) => {
                          setStatusValues(prev => prev.map((v, i) =>
                            i === idx ? { ...v, isInactive: e.target.checked } : v
                          ));
                        }}
                        className="w-5 h-5 rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-slate-800">"{sv.value}"</div>
                        <div className="text-xs text-slate-500">{sv.count} client{sv.count > 1 ? 's' : ''}</div>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        sv.isInactive
                          ? 'bg-red-100 text-red-700'
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {sv.isInactive ? 'Will Skip' : 'Will Import'}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="px-6 py-4 border-t bg-gray-50 flex gap-3">
                <button
                  onClick={() => setShowStatusModal(false)}
                  className="flex-1 px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition font-medium"
                >
                  Done - Continue Import
                </button>
              </div>
            </div>
          </div>
        )}

        {showSkippedModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
              <div className="px-6 py-4 border-b bg-gray-50 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <AlertTriangle className="text-yellow-500" size={20} />
                  Skipped Clients ({skippedClients.length})
                </h3>
                <button
                  onClick={() => setShowSkippedModal(false)}
                  className="p-1 hover:bg-gray-200 rounded-lg transition"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                <p className="text-sm text-slate-600 mb-4">
                  The following clients were not imported because they had an inactive or lost status in the carrier export.
                  These are typically clients who have left the carrier or cancelled their policy.
                </p>
                <div className="space-y-2">
                  {skippedClients.map((client, idx) => (
                    <div key={idx} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="font-medium text-slate-800">{client.name}</div>
                      <div className="text-sm text-slate-500">{client.reason}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="px-6 py-4 border-t bg-gray-50 flex gap-3">
                <button
                  onClick={() => {
                    const csv = 'Name,Reason\n' + skippedClients.map(c => `"${c.name}","${c.reason}"`).join('\n');
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'skipped-clients.csv';
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100 transition text-sm font-medium text-slate-700"
                >
                  <Download size={16} />
                  Download CSV
                </button>
                <button
                  onClick={() => setShowSkippedModal(false)}
                  className="flex-1 px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Duplicate Merge Modal */}
        <DuplicateMergeModal
          isOpen={duplicateModalOpen}
          duplicate={potentialDuplicates[currentDuplicateIndex]}
          onClose={() => setDuplicateModalOpen(false)}
          onSave={handleDuplicateSave}
          onSkip={handleDuplicateSkip}
          currentIndex={currentDuplicateIndex + 1}
          totalDuplicates={potentialDuplicates.length}
        />
      </div>
    </div>
  );
}

// Mapping field component
function MappingField({ field, label, desc, headers, value, onChange, required, critical }) {
  console.log(`[MappingField ${field}] headers:`, headers, 'length:', headers?.length);
  const isMapped = value != null && value !== '';

  return (
    <div className={`rounded-xl p-4 border transition ${
      required
        ? isMapped ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
        : critical
          ? isMapped ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'
          : isMapped ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
    }`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-slate-700">{label}</span>
        {isMapped && <Check size={16} className="text-green-600" />}
      </div>
      <select
        className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
        value={value ?? ""}
        onChange={(e) => onChange(field, e.target.value)}
      >
        <option value="">— Select column —</option>
        {headers.map((h, i) => (
          <option key={i} value={i}>
            {i + 1}. {h}
          </option>
        ))}
      </select>
      <div className="text-xs text-slate-500 mt-2">{desc}</div>
    </div>
  );
}
